import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, type DebugUserProfile } from '../shared/api-client';
import { DebugUserPicker } from '../shared/components/DebugUserPickerComponent';
import './LoginPage.css';

const ERROR_MESSAGES: Record<string, string> = {
  oauth_not_configured: 'OAuth is not configured. Please contact an administrator.',
  invalid_state: 'Authentication failed due to invalid state. Please try again.',
  missing_code: 'Authentication failed. Please try again.',
  token_exchange_failed: 'Failed to authenticate with Google. Please try again.',
  userinfo_failed: 'Failed to get user information from Google. Please try again.',
  invalid_domain: 'Your email domain is not authorized. Only @kibocommerce.com accounts are allowed.',
  auth_failed: 'Authentication failed. Please try again.',
  access_denied: 'Access was denied. Please try again.',
};

/**
 * Validates that a returnTo URL is safe to redirect to.
 * Only allows relative paths within the app (no external URLs or protocol handlers).
 */
function isValidReturnTo(url: string | null): boolean {
  if (!url) return false;
  // Must start with / (relative path)
  if (!url.startsWith('/')) return false;
  // Prevent protocol-relative URLs (//example.com)
  if (url.startsWith('//')) return false;
  // Block known dangerous paths
  if (url.startsWith('/login') || url.startsWith('/logout')) return false;
  return true;
}

export function Login(): JSX.Element {
  const [searchParams] = useSearchParams();
  const error = searchParams.get('error');
  const errorMessage = error ? ERROR_MESSAGES[error] ?? 'An error occurred. Please try again.' : null;

  // Get and validate returnTo parameter
  const rawReturnTo = searchParams.get('returnTo');
  const returnTo = isValidReturnTo(rawReturnTo) ? rawReturnTo : null;

  const [debugProfiles, setDebugProfiles] = useState<DebugUserProfile[] | null>(null);
  const [debugAuthChecked, setDebugAuthChecked] = useState(false);
  const [serverRetrying, setServerRetrying] = useState(false);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    document.title = 'Login - Kibo Business Automation Hub';

    async function checkDebugProfiles(): Promise<void> {
      try {
        const result = await api.getDebugProfiles();
        if (result.success && result.data.enabled) {
          setDebugProfiles(result.data.profiles);
        }
        setServerRetrying(false);
        setDebugAuthChecked(true);
      } catch {
        // Network error / server not ready — retry
        setServerRetrying(true);
        retryTimerRef.current = setTimeout(() => { void checkDebugProfiles(); }, 1000);
      }
    }

    void checkDebugProfiles();

    return () => {
      if (retryTimerRef.current !== null) {
        clearTimeout(retryTimerRef.current);
      }
    };
  }, []);

  const handleGoogleLogin = (): void => {
    // Pass returnTo to the OAuth flow if valid
    const url = returnTo
      ? `/auth/google?returnTo=${encodeURIComponent(returnTo)}`
      : '/auth/google';
    window.location.href = url;
  };

  // Don't render until we've checked for debug auth
  if (!debugAuthChecked || serverRetrying) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-card">
            <div className="login-header">
              <div className="login-logo">
                <img src="/images/Kibo-FC-Black.png" alt="Kibo" height="40" />
              </div>
              <h1 className="login-title">Kibo Hub</h1>
              <p className="login-subtitle">
                {serverRetrying ? 'Waiting for server...' : 'Loading...'}
              </p>
            </div>
            {serverRetrying && <div className="login-spinner" />}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <div className="login-logo">
              <img src="/images/Kibo-FC-Black.png" alt="Kibo" height="40" />
            </div>
            <h1 className="login-title">Kibo Business Automation Hub</h1>
            <p className="login-subtitle">
              {debugProfiles ? 'Select a debug user' : 'Sign in to access your tools'}
            </p>
          </div>

          {errorMessage && (
            <div className="login-error">
              {errorMessage}
            </div>
          )}

          {debugProfiles ? (
            <DebugUserPicker profiles={debugProfiles} returnTo={returnTo} />
          ) : (
            <>
              <button onClick={handleGoogleLogin} className="google-btn">
                <svg className="google-icon" viewBox="0 0 24 24" width="24" height="24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                <span>Sign in with Google</span>
              </button>

              <p className="login-note">
                Only @kibocommerce.com accounts are authorized.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
